import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FiEye, FiEdit3, FiCopy, FiDownload, FiList, FiHash, FiBold, FiItalic, FiCode, FiCheckSquare, FiMessageSquare, FiUnderline } from 'react-icons/fi';
import styles from '../styles/notesTab.module.css';

const toolbarSnippets = [
  { key: 'h1', icon: <FiHash size={14} />, label: 'H1', insert: '# ' },
  { key: 'bold', icon: <FiBold size={14} />, label: 'Bold', wrap: '**' },
  { key: 'italic', icon: <FiItalic size={14} />, label: 'Italic', wrap: '*' },
  { key: 'underline', icon: <FiUnderline size={14} />, label: 'Underline', wrap: '<u>', wrapClose: '</u>' },
  { key: 'code', icon: <FiCode size={14} />, label: 'Code', wrap: '`' },
  { key: 'quote', icon: <FiMessageSquare size={14} />, label: 'Quote', insert: '> ' },
  { key: 'list', icon: <FiList size={14} />, label: 'List', insert: '- ' },
  { key: 'task', icon: <FiCheckSquare size={14} />, label: 'Task', insert: '- [ ] ' },
];

const NotesTab = ({ chatId }) => {
  const resolvedChatId = chatId || (typeof window !== 'undefined' ? localStorage.getItem('activeChatId') : null) || 'unknown';
  const storageKey = `chatNotes_${resolvedChatId}`;
  const [mode, setMode] = useState('edit');
  const [value, setValue] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    setValue(saved ? JSON.parse(saved) : '');
  }, [storageKey]);

  useEffect(() => {
    const id = setTimeout(() => {
      localStorage.setItem(storageKey, JSON.stringify(value || ''));
    }, 300);
    return () => clearTimeout(id);
  }, [value, storageKey]);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(value || ''); } catch {}
  };

  const handleDownload = () => {
    const blob = new Blob([value || ''], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${resolvedChatId || 'notes'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const textareaRef = React.useRef(null);

  const applySnippet = useCallback((snip) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    let nextValue;
    if (snip.wrap) {
      const close = snip.wrapClose || snip.wrap;
      nextValue = value.slice(0, start) + snip.wrap + selected + close + value.slice(end);
      setValue(nextValue);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(start + snip.wrap.length, end + snip.wrap.length);
      });
    } else if (snip.insert) {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      nextValue = value.slice(0, lineStart) + snip.insert + value.slice(lineStart);
      setValue(nextValue);
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(start + snip.insert.length, start + snip.insert.length);
      });
    }
  }, [value]);

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <div className={styles.title}>Notes</div>
        <div className={styles.actions}>
          {toolbarSnippets.map(s => (
            <button
              key={s.key}
              className={styles.toolBtn}
              onClick={() => applySnippet(s)}
              title={s.label}
            >
              {s.icon}
            </button>
          ))}
          <span className={styles.sep} />
          <button className={`${styles.modeBtn} ${mode==='edit'?styles.active:''}`} onClick={() => setMode('edit')} title="Edit"><FiEdit3 size={14} /> Edit</button>
          <button className={`${styles.modeBtn} ${mode==='preview'?styles.active:''}`} onClick={() => setMode('preview')} title="Preview"><FiEye size={14} /> Preview</button>
          <span className={styles.sep} />
          <button className={styles.iconBtn} onClick={handleCopy} title="Copy all"><FiCopy size={14} /></button>
          <button className={styles.iconBtn} onClick={handleDownload} title="Download .md"><FiDownload size={14} /></button>
        </div>
      </header>

      <div className={styles.body}>
        {mode === 'edit' ? (
          <textarea
            ref={textareaRef}
            className={styles.editor}
            placeholder="Type '/' for commands, or start writing..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <div className={styles.preview}>
            <div className={styles.doc}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{value || ''}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      <footer className={styles.footer}>
        <span className={styles.hint}>Autosaved • Markdown + checklists supported</span>
      </footer>
    </div>
  );
};

export default React.memo(NotesTab);
